/**
 * MODULE: domain/pages/frontend/components/editor/ActionButtonModal.tsx
 * PURPOSE: Configure label, link target, and style for an action button block
 */

import { useEffect, useState } from 'react';
import { Button, Checkbox, Label, Modal, ModalBody, ModalFooter, ModalHeader, Select, TextInput } from 'flowbite-react';
import { HiOutlineCursorClick } from 'react-icons/hi';
import {
  buildActionButtonContent,
  type ActionButtonBlockContent,
  type ActionButtonLinkType,
  type ActionButtonVariant,
} from '../../utils/pageActionButtonUtils';
import { PageLinkPickerModal } from './PageLinkPickerModal';

type Props = {
  open: boolean;
  workspaceId: string;
  currentPageId: string;
  initial?: ActionButtonBlockContent | null;
  onClose: () => void;
  onSubmit: (content: ActionButtonBlockContent) => void;
};

export function ActionButtonModal({
  open,
  workspaceId,
  currentPageId,
  initial,
  onClose,
  onSubmit,
}: Props) {
  const [label, setLabel] = useState('Get started');
  const [linkType, setLinkType] = useState<ActionButtonLinkType>('external');
  const [url, setUrl] = useState('');
  const [pageId, setPageId] = useState<string | null>(null);
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const [variant, setVariant] = useState<ActionButtonVariant>('primary');
  const [openInNewTab, setOpenInNewTab] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagePickerOpen, setPagePickerOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLabel(initial?.label ?? 'Get started');
    setLinkType(initial?.link_type ?? 'external');
    setUrl(initial?.url ?? '');
    setPageId(initial?.page_id ?? null);
    setPageTitle(initial?.page_title ?? null);
    setVariant(initial?.variant ?? 'primary');
    setOpenInNewTab(initial?.open_in_new_tab ?? initial?.link_type !== 'page');
    setError(null);
    setPagePickerOpen(false);
  }, [initial, open]);

  const handleSubmit = () => {
    const content = buildActionButtonContent({
      label,
      linkType,
      url,
      pageId,
      workspaceId: linkType === 'page' ? workspaceId : null,
      pageTitle,
      variant,
      openInNewTab,
    });
    if (!content) {
      setError(
        linkType === 'page'
          ? 'Choose a page and enter button label.'
          : 'Enter a valid https URL and button label.'
      );
      return;
    }
    onSubmit(content);
    onClose();
  };

  return (
    <>
      <Modal show={open} onClose={onClose} size="md">
        <ModalHeader>{initial ? 'Edit action button' : 'Add action button'}</ModalHeader>
        <ModalBody data-testid="pages-action-button-modal">
          <div>
            <Label htmlFor="pages-action-button-label" className="mb-1 block text-sm">
              Button label
            </Label>
            <TextInput
              id="pages-action-button-label"
              sizing="sm"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="e.g. Open deal room"
              data-testid="pages-action-button-label-input"
            />
          </div>
          <div className="mt-3">
            <Label htmlFor="pages-action-button-link-type" className="mb-1 block text-sm">
              Link to
            </Label>
            <Select
              id="pages-action-button-link-type"
              sizing="sm"
              value={linkType}
              onChange={(event) => setLinkType(event.target.value as ActionButtonLinkType)}
              data-testid="pages-action-button-link-type"
            >
              <option value="external">External URL</option>
              <option value="page">Workspace page</option>
            </Select>
          </div>
          {linkType === 'external' ? (
            <div className="mt-3">
              <Label htmlFor="pages-action-button-url" className="mb-1 block text-sm">
                URL
              </Label>
              <TextInput
                id="pages-action-button-url"
                sizing="sm"
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                placeholder="https://…"
                data-testid="pages-action-button-url-input"
              />
            </div>
          ) : (
            <div className="mt-3">
              <Label className="mb-1 block text-sm">Page</Label>
              <div className="flex items-center gap-2">
                <span
                  className="min-w-0 flex-1 truncate rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-700"
                  data-testid="pages-action-button-page-label"
                >
                  {pageTitle ?? 'No page selected'}
                </span>
                <Button
                  size="sm"
                  color="light"
                  onClick={() => setPagePickerOpen(true)}
                  data-testid="pages-action-button-choose-page"
                >
                  Choose…
                </Button>
              </div>
            </div>
          )}
          <div className="mt-3">
            <Label htmlFor="pages-action-button-variant" className="mb-1 block text-sm">
              Style
            </Label>
            <Select
              id="pages-action-button-variant"
              sizing="sm"
              value={variant}
              onChange={(event) => setVariant(event.target.value as ActionButtonVariant)}
              data-testid="pages-action-button-variant"
            >
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
              <option value="outline">Outline</option>
            </Select>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Checkbox
              id="pages-action-button-new-tab"
              checked={openInNewTab}
              onChange={(event) => setOpenInNewTab(event.target.checked)}
              data-testid="pages-action-button-new-tab"
            />
            <Label htmlFor="pages-action-button-new-tab" className="text-sm text-gray-700 dark:text-gray-300">
              Open in new tab
            </Label>
          </div>
          {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
        </ModalBody>
        <ModalFooter>
          <Button color="gray" onClick={onClose}>
            Cancel
          </Button>
          <Button color="blue" onClick={handleSubmit} data-testid="pages-action-button-submit">
            <HiOutlineCursorClick className="mr-1.5 h-4 w-4" aria-hidden />
            {initial ? 'Save button' : 'Insert button'}
          </Button>
        </ModalFooter>
      </Modal>
      {pagePickerOpen ? (
        <PageLinkPickerModal
          open={pagePickerOpen}
          workspaceId={workspaceId}
          currentPageId={currentPageId}
          onClose={() => setPagePickerOpen(false)}
          onSelect={(page) => {
            setPageId(page.id);
            setPageTitle(page.title);
            setPagePickerOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
